import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { usePageBreadcrumbs } from "@/context/PageNavigation";
import type { BreadcrumbItem } from "@/lib/pageNavigation";
import { cn } from "@/lib/cn";

const CRUMB_CLASS = [
  "inline-flex min-w-0 max-w-full items-center rounded-md px-1.5 py-1",
  "text-xs font-medium leading-none transition-colors duration-150",
].join(" ");

const LINK_CLASS = [
  CRUMB_CLASS,
  "text-bridge-muted hover:bg-bridge-accent/10 hover:text-bridge-accent",
  "focus-ring focus-visible:bg-bridge-accent/10 focus-visible:text-bridge-accent",
].join(" ");

export function Breadcrumbs({ items }: { items?: BreadcrumbItem[] }) {
  const pageItems = usePageBreadcrumbs();
  const crumbs = items ?? pageItems;
  if (!crumbs.length) return null;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 overflow-hidden">
      <ol className="flex min-w-0 items-center gap-0.5" role="list">
        {crumbs.map((crumb, index) => {
          const current = index === crumbs.length - 1;
          return (
            <li
              key={crumb.to || `${crumb.label}-${index}`}
              className={cn(
                "flex items-center gap-0.5",
                current ? "min-w-0 flex-1" : "shrink-0",
              )}
            >
              {index > 0 && (
                <ChevronRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 text-bridge-subtle/70"
                  strokeWidth={1.75}
                />
              )}
              {current ? (
                <span
                  aria-current="page"
                  title={crumb.label}
                  className={cn(CRUMB_CLASS, "font-semibold text-bridge-fg")}
                >
                  <span className="truncate">{crumb.label}</span>
                </span>
              ) : crumb.onClick ? (
                <button
                  type="button"
                  onClick={crumb.onClick}
                  className={cn(LINK_CLASS, "shrink-0")}
                  title={crumb.label}
                >
                  {crumb.label}
                </button>
              ) : crumb.to ? (
                <Link to={crumb.to} className={cn(LINK_CLASS, "shrink-0")} title={crumb.label}>
                  {crumb.label}
                </Link>
              ) : (
                <span className={cn(CRUMB_CLASS, "shrink-0 text-bridge-muted")}>{crumb.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
